/**
 * Ricerca manuale di strumenti (F-085) — la voce «Strumenti» che era in
 * sidebar da sempre come segnaposto di F-013.
 *
 * Cerca per ticker, ISIN, nome e mercato mettendo insieme tre fonti: il nostro
 * catalogo, OpenFIGI e il catalogo del broker. Il backend fonde le righe e
 * dichiara l'esito di ciascuna fonte; qui si tratta di non appiattire quelle
 * distinzioni sullo schermo.
 *
 * ## Tre cose che questa pagina si rifiuta di far sembrare vere
 *
 * 1. **«Nessun risultato» solo quando tutte le fonti hanno risposto.** Se
 *    OpenFIGI era irraggiungibile, un elenco vuoto sarebbe una risposta falsa:
 *    la striscia delle fonti lo dice, e non è un dettaglio diagnostico —
 *    cambia la conclusione che l'utente trae.
 * 2. **Un titolo a catalogo non è per questo apribile.** `instruments` è
 *    un'anagrafica condivisa; la scheda è riservata a chi quel titolo lo
 *    segue. Il nome diventa un collegamento solo quando `visible` è vero, e
 *    altrimenti la riga offre il gesto che lo rende visibile invece di un
 *    link che finisce su «Titolo non disponibile».
 * 3. **Una valuta non si indovina.** Aggiungere al catalogo un candidato che
 *    non dichiara la valuta la chiede: è la stessa regola di F-043, e finisce
 *    nelle quotazioni, dove un valore sbagliato rende il prezzo illeggibile.
 *    Il rifiuto vero sta comunque nel server, che senza valuta non crea nulla.
 *
 * La ricerca parte **su invio**, mai a ogni battitura: raggiunge servizi
 * esterni a limite di frequenza stretto.
 */

import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-client';
import { Alert, Badge, Button, Card, DomainBadge, EmptyState, Field, Select, Spinner, TableWrap } from '@/ui';
import { useMarkets } from '@/features/markets/api';
import { useCreateInstrument } from '@/features/portfolios/api';
import { useAddWatchlistItem } from '@/features/watchlist/api';
import { formatExchangeCode } from '@/features/watchlist/openfigi-exchange-codes';
import { CommercialName } from './CommercialName';
import { formatNumber } from '@/lib/format';
import {
  instrumentSearchKeys,
  useInstrumentSearch,
  type InstrumentSearchHit,
  type InstrumentSearchParams,
  type SearchSourceName,
  type SearchSourceOutcome,
  CRYPTO_MIC,
  SOURCES_BY_DOMAIN,
  searchDomainOf,
  useCreateCryptoInstrument,
} from './search-api';
import './instruments.css';

/** Le fonti proposte dipendono dal mercato scelto (SPEC-026 §2, F-165). */
const ALL_SOURCES: SearchSourceName[] = SOURCES_BY_DOMAIN.stock;

const SOURCE_LABELS: Record<SearchSourceName, string> = {
  catalog: 'Catalogo',
  openfigi: 'OpenFIGI',
  trading212: 'Trading 212',
  coingecko: 'CoinGecko',
  exchange: 'Exchange',
};

const STATUS_LABELS: Record<SearchSourceOutcome['status'], string> = {
  ok: 'ha risposto',
  unavailable: 'non disponibile',
  skipped: 'esclusa',
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

/** Esito per fonte. Non è diagnostica: dice se «nessun risultato» è una risposta. */
function SourceStrip({ sources }: { sources: SearchSourceOutcome[] }) {
  return (
    <ul className="isearch__sources">
      {sources.map((source) => (
        <li key={source.source} className={`isearch__source isearch__source--${source.status}`}>
          <span className="isearch__source-name">{SOURCE_LABELS[source.source]}</span>
          <span className="isearch__source-status">
            {source.status === 'ok' ? `${source.count} righe` : STATUS_LABELS[source.status]}
          </span>
          {source.detail && <span className="isearch__source-detail">{source.detail}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * Aggiunge al catalogo un candidato che ancora non c'è.
 *
 * Compare solo per le righe che portano una **borsa**: il ticker del broker
 * (`AAPL_US_EQ`) non è un simbolo di quotazione, e scriverlo a catalogo è
 * l'ambiguità che `broker-ticker.service.ts` esiste per non ripetere.
 */
function AddToCatalogButton({
  hit,
  queryIsin,
  onCreated,
}: {
  hit: InstrumentSearchHit;
  queryIsin: string | null;
  onCreated: (instrumentId: string) => void;
}) {
  const queryClient = useQueryClient();
  const create = useCreateInstrument();
  const [askCurrency, setAskCurrency] = useState(false);
  const [currency, setCurrency] = useState('');

  // ⚠️ L'ISIN della ricerca vale **solo** per le righe OpenFIGI, ed è una
  // deduzione legittima soltanto lì: quando la ricerca è un ISIN, quei
  // candidati arrivano da `/v3/mapping` su quell'ISIN, cioè sono le venue di
  // quel titolo. Una riga del broker ha il proprio ISIN o è stata trovata per
  // testo, e timbrarle addosso quello cercato sarebbe un'invenzione.
  const isin = hit.isin ?? (hit.sources.includes('openfigi') ? queryIsin : null);

  const run = (chosen: string) => {
    create.mutate(
      {
        ticker: hit.ticker!,
        exchange: hit.exchange,
        figi: hit.figi,
        currency: chosen,
        securityType: hit.instrumentType,
        ...(hit.name ? { name: hit.name } : {}),
        // ISIN e ticker del broker erano nella riga e andavano persi: la
        // ricerca li aveva appena mostrati, la creazione non li passava, e
        // ritrovarli significava riaprire il dialog di associazione. Peggio:
        // senza ISIN il titolo appena creato non si fonde piu' con la riga di
        // ricerca — la fusione avviene su identificatori che identificano
        // (F-085) — e la riga continuava a offrire «+ Al catalogo» per qualcosa
        // che a catalogo era gia'.
        ...(isin ? { isin } : {}),
        ...(hit.trading212Ticker ? { trading212Ticker: hit.trading212Ticker } : {}),
      },
      {
        onSuccess: (created) => {
          // ⚠️ **La riga si aggiorna dal risultato, non dalla rilettura.** La
          // rilettura c'e' e serve (le altre righe e il conteggio per fonte
          // sono cambiati), ma non basta a dare una risposta: rifa' la ricerca
          // su OpenFIGI, quindi arriva secondi dopo, e puo' benissimo
          // riportare la riga **identica** — se il candidato non si fonde con
          // l'anagrafica appena creata, «+ Al catalogo» resta li' e sembra che
          // il clic non abbia fatto niente. E' cio' che succedeva cercando per
          // ISIN: le righe OpenFIGI non lo portano, il titolo nasceva senza, e
          // il catalogo non lo ritrovava con quella chiave.
          onCreated(created.id);
          setAskCurrency(false);
          void queryClient.invalidateQueries({ queryKey: instrumentSearchKeys.all });
        },
      },
    );
  };

  if (askCurrency) {
    return (
      <span className="isearch__currency-ask">
        <input
          className="field__input"
          aria-label={`Valuta di quotazione per ${hit.name}`}
          value={currency}
          onChange={(event) => setCurrency(event.target.value)}
          placeholder="EUR"
          maxLength={3}
        />
        <Button
          variant="ghost"
          loading={create.isPending}
          disabled={!/^[A-Za-z]{3}$/.test(currency.trim())}
          onClick={() => run(currency.trim().toUpperCase())}
        >
          Conferma
        </Button>
      </span>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        loading={create.isPending}
        onClick={() => {
          if (hit.currency) {
            run(hit.currency);
            return;
          }
          // OpenFIGI non dichiara quasi mai la valuta nella ricerca per nome:
          // la si chiede, mai la si deduce dalla borsa.
          setCurrency('');
          setAskCurrency(true);
        }}
      >
        + Al catalogo
      </Button>
      {create.isError && (
        <p className="isearch__row-error" role="alert">
          {errorMessage(create.error)}
        </p>
      )}
    </>
  );
}

/**
 * Una crypto entra a catalogo per simbolo (F-165): niente valuta da chiedere,
 * niente borsa — il server la fa nascere come la scoperta (SPEC-026 §3.4).
 */
function AddCryptoToCatalogButton({
  hit,
  onCreated,
}: {
  hit: InstrumentSearchHit;
  onCreated: (instrumentId: string) => void;
}) {
  const queryClient = useQueryClient();
  const create = useCreateCryptoInstrument();
  const symbol = hit.symbol ?? hit.ticker;
  if (!symbol) return <span className="isearch__note">nessun simbolo</span>;
  return (
    <>
      <Button
        variant="ghost"
        loading={create.isPending}
        onClick={() =>
          create.mutate(
            { symbol, ...(hit.name ? { name: hit.name } : {}) },
            {
              onSuccess: (created) => {
                onCreated(created.id);
                void queryClient.invalidateQueries({ queryKey: instrumentSearchKeys.all });
              },
            },
          )
        }
      >
        + Al catalogo
      </Button>
      {create.isError && (
        <p className="isearch__row-error" role="alert">
          {errorMessage(create.error)}
        </p>
      )}
    </>
  );
}

/** Rende visibile un titolo già a catalogo mettendolo in watchlist. */
function MakeVisibleButton({ hit }: { hit: InstrumentSearchHit }) {
  const addItem = useAddWatchlistItem();
  return (
    <>
      <Button
        variant="ghost"
        loading={addItem.isPending}
        title={`Aggiungi ${hit.name} alla watchlist per poterne aprire la scheda`}
        onClick={() => addItem.mutate({ instrumentId: hit.instrumentId! })}
      >
        ☆ Segui
      </Button>
      {addItem.isError && (
        <p className="isearch__row-error" role="alert">
          {errorMessage(addItem.error)}
        </p>
      )}
    </>
  );
}

function ResultRow({
  hit,
  queryIsin,
  onSearchIsin,
}: {
  hit: InstrumentSearchHit;
  queryIsin: string | null;
  onSearchIsin: (isin: string) => void;
}) {
  /**
   * Il titolo creato da questa riga, finché la rilettura non lo racconta da
   * sola. Vive qui e non dentro il pulsante perché è la **riga** a cambiare
   * natura: da candidato esterno a titolo di catalogo, con le azioni che ne
   * conseguono. Sopravvive alla rilettura — la chiave della riga è stabile —
   * e sparisce solo quando la ricerca viene rifatta da capo.
   */
  const [createdId, setCreatedId] = useState<string | null>(null);
  const instrumentId = hit.instrumentId ?? createdId;

  return (
    <tr>
      <td>
        {hit.visible && instrumentId ? (
          <Link to={`/instruments/${instrumentId}`}>{hit.name}</Link>
        ) : (
          <span className="isearch__name">{hit.name}</span>
        )}
        {/* F-087: è il motivo per cui il TODO chiedeva questo nome — una
            ricerca per ticker restituisce simboli che si somigliano. */}
        <CommercialName name={hit.name} commercialName={hit.commercialName} size="small" />
        {instrumentId && !hit.visible && (
          <span className="isearch__note">
            {createdId && !hit.instrumentId ? 'aggiunto al catalogo' : 'non fra i titoli che segui'}
          </span>
        )}
      </td>
      <td className="isearch__ident">
        {hit.isin ? (
          // Un ISIN è la domanda più precisa che si possa fare a OpenFIGI
          // (`/v3/mapping`, che restituisce anche la valuta): cliccarlo rifà la
          // ricerca da lì, che è la via d'uscita per una riga del solo broker.
          <button type="button" className="isearch__isin" onClick={() => onSearchIsin(hit.isin!)}>
            {hit.isin}
          </button>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="isearch__ident">
        {hit.symbol ?? hit.ticker ?? '—'}
        {/* F-165: per una moneta contano il rank e se e' investibile (top 200,
            SPEC-026 §9), non la borsa. */}
        {hit.domain === 'crypto' && (
          <span className="isearch__note">
            {hit.marketCapRank !== null ? `#${formatNumber(hit.marketCapRank)} per capitalizzazione` : 'rank non misurato'}
            {hit.investable && !hit.investable.ok && hit.investable.reason ? ` · ${hit.investable.reason}` : ''}
          </span>
        )}
      </td>
      <td className="col--secondary">
        {hit.domain === 'crypto' ? (hit.exchangePair ?? '—') : formatExchangeCode(hit.exchange)}
      </td>
      <td className="col--secondary">{hit.marketName ?? (hit.mic ?? '—')}</td>
      <td>{hit.currency ?? '—'}</td>
      <td>
        <span className="isearch__badges">
          {hit.sources.map((source) => (
            <Badge key={source} tone={source === 'catalog' ? 'positive' : 'neutral'}>
              {SOURCE_LABELS[source]}
            </Badge>
          ))}
          <DomainBadge domain={hit.domain} />
        </span>
      </td>
      <td className="isearch__actions">
        {hit.visible ? (
          <span className="text-muted">—</span>
        ) : instrumentId ? (
          // Il titolo esiste a catalogo — che lo sapessimo già o che lo abbia
          // appena creato questa riga: il passo successivo è lo stesso.
          <MakeVisibleButton hit={{ ...hit, instrumentId }} />
        ) : hit.domain === 'crypto' ? (
          <AddCryptoToCatalogButton hit={hit} onCreated={setCreatedId} />
        ) : hit.ticker && hit.exchange ? (
          <AddToCatalogButton hit={hit} queryIsin={queryIsin} onCreated={setCreatedId} />
        ) : (
          <span className="isearch__note">
            {hit.isin ? "cerca per ISIN per identificarlo" : 'nessun simbolo di quotazione'}
          </span>
        )}
      </td>
    </tr>
  );
}

export function InstrumentSearchPage() {
  const [term, setTerm] = useState('');
  const [mic, setMic] = useState('');
  const [sources, setSources] = useState<SearchSourceName[]>(ALL_SOURCES);
  // L'ambito lo decide il mercato (SPEC-026 §2): cambiarlo cambia le fonti,
  // perche' OpenFIGI e il broker non sanno nulla di una moneta, e CoinGecko
  // nulla di un'azione. Le caselle si azzerano sulle fonti del nuovo ambito.
  const domain = searchDomainOf(mic);
  const domainSources = SOURCES_BY_DOMAIN[domain];
  const chooseMarket = (nextMic: string) => {
    setMic(nextMic);
    if (searchDomainOf(nextMic) !== domain) setSources(SOURCES_BY_DOMAIN[searchDomainOf(nextMic)]);
  };
  const [submitted, setSubmitted] = useState<InstrumentSearchParams | null>(null);

  const markets = useMarkets();
  const search = useInstrumentSearch(submitted);

  const marketOptions = [
    { value: '', label: 'Tutti i mercati' },
    ...(markets.data?.items ?? []).map((market) => ({ value: market.mic, label: `${market.name} (${market.mic})` })),
  ];

  const run = (query: string, withMic = mic, withSources = sources) => {
    const trimmed = query.trim();
    if (trimmed.length < 2 || withSources.length === 0) return;
    setSubmitted({ q: trimmed, mic: withMic, sources: withSources });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(term);
  };

  /** Una ricerca per ISIN parte dalla riga: si aggiorna anche il campo, o la pagina mostrerebbe un termine diverso da quello cercato. */
  const searchByIsin = (isin: string) => {
    setTerm(isin);
    // Il mercato si azzera di proposito: un ISIN identifica il titolo, e un
    // filtro rimasto acceso lo farebbe sparire dai risultati senza dirlo.
    chooseMarket('');
    run(isin, '', SOURCES_BY_DOMAIN.stock);
  };

  const toggleSource = (source: SearchSourceName) => {
    setSources((current) =>
      current.includes(source) ? current.filter((entry) => entry !== source) : [...current, source],
    );
  };

  const result = search.data;
  const truncated = result ? result.total > result.items.length : false;

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1>Strumenti</h1>
          <p className="page__subtitle">
            Cerca un titolo per ticker, ISIN, nome o mercato nel catalogo, su OpenFIGI e nel catalogo
            del broker; una criptovaluta — mercato «{CRYPTO_MIC}» — su CoinGecko e nel catalogo del tuo
            exchange. I risultati che descrivono lo stesso titolo vengono uniti.
          </p>
        </div>
      </header>

      <Card title="Cerca">
        <form className="isearch__form" onSubmit={handleSubmit}>
          <Field
            label="Ticker, ISIN o nome"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="AAPL, US0378331005, Apple…"
            hint="Almeno due caratteri: la ricerca interroga anche servizi esterni"
            maxLength={120}
          />
          <Select
            label="Mercato"
            value={mic}
            onChange={(event) => chooseMarket(event.target.value)}
            options={marketOptions}
            hint={domain === 'crypto' ? 'Ambito crypto: le fonti sono CoinGecko e il tuo exchange' : undefined}
          />
          <fieldset className="isearch__fonti">
            <legend>Fonti</legend>
            {domainSources.map((source) => (
              <label key={source}>
                <input
                  type="checkbox"
                  checked={sources.includes(source)}
                  onChange={() => toggleSource(source)}
                />
                {SOURCE_LABELS[source]}
              </label>
            ))}
          </fieldset>
          <Button type="submit" variant="primary" loading={search.isFetching} disabled={term.trim().length < 2 || sources.length === 0}>
            Cerca
          </Button>
        </form>

        {sources.length === 0 && (
          <Alert variant="warning" title="Nessuna fonte selezionata">
            Senza almeno una fonte non c'è dove cercare.
          </Alert>
        )}
      </Card>

      {search.isError && (
        <Alert variant="error" title="Ricerca non riuscita">
          {errorMessage(search.error)}
        </Alert>
      )}

      {search.isPending && submitted && <Spinner label="Ricerca in corso…" />}

      {result && (
        <Card
          title={`Risultati per «${result.query}»`}
          subtitle={truncated ? `Primi ${result.items.length} di ${result.total}` : undefined}
        >
          <SourceStrip sources={result.sources} />

          {result.excludedByMarket > 0 && (
            <p className="isearch__excluded">
              {result.excludedByMarket} candidati delle fonti esterne esclusi dal filtro di mercato:
              la loro borsa non si traduce in un mercato che conosciamo.
            </p>
          )}

          {result.items.length === 0 ? (
            <EmptyState
              icon="🔍"
              title="Nessun titolo trovato"
              description={
                result.sources.some((source) => source.status !== 'ok')
                  ? "Attenzione: non tutte le fonti hanno risposto, quindi l'elenco vuoto non è una risposta completa."
                  : 'Prova con l\'ISIN o il simbolo esatto.'
              }
            />
          ) : (
            <TableWrap>
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>ISIN</th>
                    <th>Simbolo</th>
                    <th>Borsa</th>
                    <th>Mercato</th>
                    <th>Valuta</th>
                    <th>Fonti</th>
                    <th aria-label="Azioni" />
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((hit) => (
                    <ResultRow
                      key={hit.key}
                      hit={hit}
                      queryIsin={result.queryIsin}
                      onSearchIsin={searchByIsin}
                    />
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </Card>
      )}
    </div>
  );
}
